<template>
  <public-layout
    title="Privacy Policy"
    subtitle="We value your privacy. Learn how we collect, use, and protect your personal information."
  >
    <section class="sc-section">
      <div class="sc-container">
        <div class="legal-layout">
          <!-- Sidebar -->
          <aside class="legal-sidebar">
            <div class="legal-sidebar__inner">
              <h4>Legal Documents</h4>
              <nav class="legal-nav">
                <router-link to="/terms-and-conditions" class="legal-nav__item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Terms &amp; Conditions
                </router-link>
                <router-link to="/privacy-policy" class="legal-nav__item legal-nav__item--active">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  Privacy Policy
                </router-link>
              </nav>
              <div class="legal-sidebar__note">
                <p>Last updated: 2024</p>
                <p>Questions? <a href="/contact">Contact us</a></p>
              </div>
            </div>
          </aside>

          <!-- Content -->
          <div class="legal-content">
            <div class="legal-content__header">
              <h1>Privacy Policy</h1>
              <p class="legal-intro">
                This Privacy Policy describes how Structurecity collects, uses, and shares information
                about you when you use our platform and services.
              </p>
            </div>

            <div v-if="loading" class="legal-skeleton">
              <div v-for="n in 6" :key="n" class="legal-skeleton__block">
                <div class="skeleton" style="height:20px;width:40%;border-radius:4px;margin-bottom:1rem;"></div>
                <div class="skeleton" style="height:14px;width:100%;border-radius:4px;margin-bottom:.5rem;"></div>
                <div class="skeleton" style="height:14px;width:95%;border-radius:4px;margin-bottom:.5rem;"></div>
                <div class="skeleton" style="height:14px;width:80%;border-radius:4px;margin-bottom:2rem;"></div>
              </div>
            </div>

            <div v-else-if="company.privacy" class="legal-body" v-html="company.privacy"></div>

            <div v-else class="legal-empty">
              <p>Our privacy policy is being updated. Please check back soon.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </public-layout>
</template>

<script>
import axios from 'axios';
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'PrivacyPolicy',
  components: { PublicLayout },

  data() {
    return {
      company: {},
      loading: true,
    };
  },

  created() {
    useSEO({
      title: 'Privacy Policy',
      description: 'Read how Structurecity collects, uses, and protects your personal information on our construction and real estate platform.',
      url: SITE_ORIGIN + '/privacy-policy',
    });
    this.fetchCompany();
  },

  methods: {
    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data || {}; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.legal-layout { display: grid; grid-template-columns: 260px 1fr; gap: 3rem; align-items: start; }

.legal-sidebar__inner {
  position: sticky; top: 90px;
  background: var(--surface); border-radius: var(--radius-lg);
  padding: 1.75rem; border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
}
.legal-sidebar__inner h4 {
  font-size: .8125rem; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .06em; margin: 0 0 1rem;
}
.legal-nav { display: flex; flex-direction: column; gap: .375rem; }
.legal-nav__item {
  display: flex; align-items: center; gap: .625rem;
  padding: .625rem .875rem; border-radius: var(--radius-sm);
  font-size: .9rem; font-weight: 500; color: var(--text-muted);
  text-decoration: none; transition: background .15s, color .15s;
}
.legal-nav__item:hover { background: var(--surface-3); color: var(--text); text-decoration: none; }
.legal-nav__item--active,
.router-link-active.legal-nav__item {
  background: rgba(30,58,95,.07); color: var(--primary); font-weight: 600;
}
.legal-sidebar__note { margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid var(--border-light); }
.legal-sidebar__note p { font-size: .825rem; color: var(--text-muted); margin: 0 0 .375rem; }
.legal-sidebar__note a { color: var(--primary); font-weight: 500; text-decoration: none; }
.legal-sidebar__note a:hover { text-decoration: underline; }

.legal-content { min-width: 0; }
.legal-content__header {
  background: var(--surface); border-radius: var(--radius-lg);
  padding: 2.5rem; margin-bottom: 2rem;
  border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
}
.legal-content__header h1 { font-size: 2rem; font-weight: 800; color: var(--text); margin: 0 0 .75rem; letter-spacing: -.03em; }
.legal-intro { color: var(--text-muted); font-size: 1rem; line-height: 1.65; margin: 0; }

.legal-body {
  background: var(--surface); border-radius: var(--radius-lg);
  padding: 2.5rem; border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
  font-size: .9375rem; color: var(--text); line-height: 1.75;
}

.legal-skeleton { background: var(--surface); border-radius: var(--radius-lg); padding: 2.5rem; box-shadow: var(--shadow-sm); }
.skeleton { background: linear-gradient(90deg, var(--border-light) 25%, var(--surface-3) 50%, var(--border-light) 75%); background-size: 200% 100%; animation: shimmer 1.4s ease infinite; }
@keyframes shimmer { to { background-position: -200% 0; } }

.legal-empty { background: var(--surface); border-radius: var(--radius-lg); padding: 3rem 2.5rem; text-align: center; color: var(--text-muted); }

@media (max-width: 900px) {
  .legal-layout { grid-template-columns: 1fr; }
  .legal-sidebar__inner { position: static; }
}
</style>
